import { ReactNode, useState } from 'react';
import {
  NavigationType,
  useInRouterContext,
  useLocation,
  useNavigationType
} from 'react-router-dom';
import Box from '@mui/material/Box';
import { useReducedMotion } from 'utils/motion';
import { consumePageTransitionSkip } from './pageTransitionControl';

// On a cold load/refresh react-router reports the first navigation as POP, which would
// slide the landing page in from the left (the "back" direction). Skip the animation for
// the first page rendered this session; resets to true on full reload.
let firstRenderPending = true;

/**
 * Wraps a page so it slides + fades in on navigation: forward (PUSH/REPLACE) enters
 * from the right, back (POP) enters from the left — a lightweight push/pop feel.
 * Keyed by pathname so the animation re-runs on each route change.
 *
 * CSS-only (no transition library): it animates the ENTERING screen. It does not keep
 * the previous screen mounted to slide it out behind — that would need a route stack
 * or framer-motion. Fully disabled under reduced-motion, outside a Router (e.g. unit
 * tests / standalone screens), and skippable for one navigation via
 * suppressNextPageTransition (e.g. closing the account sheet).
 */
export const PageTransition = ({ children }: { children: ReactNode }) => {
  const inRouter = useInRouterContext();
  const reduced = useReducedMotion();

  // Outside a Router there's no navigation to animate, and the router hooks would throw.
  if (!inRouter || reduced) return <>{children}</>;

  return <AnimatedPage>{children}</AnimatedPage>;
};

const AnimatedPage = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  // Consume the suppress flag once, on mount of this page.
  const [skip] = useState(consumePageTransitionSkip);
  // Consume the first-render flag once: the initial page doesn't slide.
  const [isInitial] = useState(() => {
    const value = firstRenderPending;
    firstRenderPending = false;
    return value;
  });

  if (skip || isInitial) return <>{children}</>;

  const fromRight = navigationType !== NavigationType.Pop;

  return (
    <Box
      key={location.pathname}
      sx={{
        height: '100%',
        animation: `${
          fromRight ? 'pageInRight' : 'pageInLeft'
        } 0.28s cubic-bezier(0.22, 0.61, 0.36, 1)`,
        '@keyframes pageInRight': {
          from: { transform: 'translateX(28px)', opacity: 0 },
          to: { transform: 'translateX(0)', opacity: 1 }
        },
        '@keyframes pageInLeft': {
          from: { transform: 'translateX(-28px)', opacity: 0 },
          to: { transform: 'translateX(0)', opacity: 1 }
        }
      }}
    >
      {children}
    </Box>
  );
};
